import type { EventInput } from '@fullcalendar/core';
import type { AuthenticatedUser } from '@src/features/users';
import { formatIsoDate } from '@src/shared/lib';
import { useCallback, useMemo } from 'react';
import {
  absenceSelectionToSlot,
  absencesToEvents,
  createSingleDayBackgroundEvents,
  getDateOnly,
  getTransientEventsForSlot,
  groupAbsencesByUserDate,
  hasMixedHourlyTypes,
  usersToCalendarResources,
} from '../../lib';
import type {
  Absence,
  HolidayDate,
  ResourceUser,
  SelectedAbsence,
  TimelineSlot,
} from '../../types';

type UseTimelineCalendarDataParams = {
  absences: Absence[];
  currentUser: AuthenticatedUser | null;
  holidays: HolidayDate[];
  hoveredDateKey: string | null;
  isDayView: boolean;
  isMonthView: boolean;
  selectedAbsence: SelectedAbsence | null;
  selectedSlot: TimelineSlot | null;
  users: ResourceUser[];
  visibleRange: { start: Date; end: Date };
};

function getWeekendDatesInRange(start: Date, end: Date): Date[] {
  const dates: Date[] = [];
  const current = new Date(start);
  while (current < end) {
    const day = current.getDay();
    if (day === 0 || day === 6) {
      dates.push(new Date(current));
    }
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

export default function useTimelineCalendarData({
  absences,
  currentUser,
  holidays,
  hoveredDateKey,
  isDayView,
  isMonthView,
  selectedAbsence,
  selectedSlot,
  users,
  visibleRange,
}: UseTimelineCalendarDataParams) {
  const activeSelection = useMemo(
    () =>
      selectedSlot ??
      (selectedAbsence ? absenceSelectionToSlot(selectedAbsence) : null),
    [selectedAbsence, selectedSlot],
  );

  const allAbsencesByUserDate = useMemo(
    () => groupAbsencesByUserDate(absences),
    [absences],
  );

  const collapseMonthViewAbsences = useCallback(
    (items: Absence[]) => {
      if (isDayView) {
        return items;
      }

      const itemGroups = groupAbsencesByUserDate(items);

      return Array.from(itemGroups.values(), (group) => {
        const [firstAbsence] = group;
        const key = `${firstAbsence.userId}:${firstAbsence.dateStr}`;
        const fullGroup = allAbsencesByUserDate.get(key) ?? group;

        if (!hasMixedHourlyTypes(fullGroup)) {
          return firstAbsence;
        }

        return {
          ...firstAbsence,
          useNeutralColour: true,
        };
      });
    },
    [allAbsencesByUserDate, isDayView],
  );

  const timelineResources = useMemo(
    () => usersToCalendarResources(users, currentUser),
    [currentUser, users],
  );

  const resourceIds = useMemo(
    () => timelineResources.map((resource) => resource.id),
    [timelineResources],
  );

  const weekendBackgroundEvents = useMemo(() => {
    if (!isMonthView) {
      return [];
    }

    const weekendDates = getWeekendDatesInRange(
      visibleRange.start,
      visibleRange.end,
    );

    return weekendDates.flatMap((date) => {
      const dateIso = formatIsoDate(date);
      const nextDateIso = formatIsoDate(
        new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1),
      );
      const daySuffix = date.getDay() === 0 ? 'sunday' : 'saturday';

      return resourceIds.map((resourceId) => ({
        id: `weekend-bg-${resourceId}-${dateIso}`,
        resourceId,
        start: dateIso,
        end: nextDateIso,
        display: 'background' as const,
        classNames: ['fc-weekend-background-event', `fc-weekend-${daySuffix}`],
      }));
    });
  }, [isMonthView, resourceIds, visibleRange.start, visibleRange.end]);

  const holidayDatesSet = useMemo(
    () => new Set(holidays.map((h) => h.date)),
    [holidays],
  );

  const holidayBackgroundEvents = useMemo(() => {
    if (!isMonthView || holidays.length === 0) {
      return [];
    }

    return holidays.flatMap((holiday) => {
      const dateIso = holiday.date;
      const nextDate = new Date(`${dateIso}T00:00:00`);
      nextDate.setDate(nextDate.getDate() + 1);
      const nextDateIso = formatIsoDate(nextDate);

      return users.flatMap((user) => {
        if (holiday.branchId !== null && holiday.branchId !== user.branchId) {
          return [];
        }

        const resourceId = `user-${user.id}`;
        return [
          {
            id: `holiday-bg-${resourceId}-${dateIso}`,
            resourceId,
            start: dateIso,
            end: nextDateIso,
            display: 'background' as const,
            classNames: ['fc-holiday-background-event'],
          },
        ];
      });
    });
  }, [holidays, isMonthView, users]);

  const todayBackgroundEvents = useMemo(() => {
    if (!isMonthView) {
      return [];
    }

    return createSingleDayBackgroundEvents(
      resourceIds,
      new Date(),
      'fc-today-background-event',
      'today-bg',
      [...weekendBackgroundEvents, ...holidayBackgroundEvents],
    );
  }, [
    isMonthView,
    resourceIds,
    weekendBackgroundEvents,
    holidayBackgroundEvents,
  ]);

  const isHoveredDateToday =
    hoveredDateKey === formatIsoDate(getDateOnly(new Date()));

  const hoveredBackgroundEvents = useMemo(() => {
    if (!isMonthView || !hoveredDateKey) {
      return [];
    }

    const existingBackgroundEvents = isHoveredDateToday
      ? [...weekendBackgroundEvents, ...holidayBackgroundEvents]
      : [
          ...weekendBackgroundEvents,
          ...holidayBackgroundEvents,
          ...todayBackgroundEvents,
        ];

    return createSingleDayBackgroundEvents(
      resourceIds,
      getDateOnly(new Date(`${hoveredDateKey}T00:00:00`)),
      'fc-hovered-background-event',
      'hovered-bg',
      existingBackgroundEvents,
    );
  }, [
    holidayBackgroundEvents,
    hoveredDateKey,
    isHoveredDateToday,
    isMonthView,
    resourceIds,
    todayBackgroundEvents,
    weekendBackgroundEvents,
  ]);

  const selectedAbsenceEvents = useMemo(() => {
    if (selectedAbsence?.isMultiEvent) {
      return [];
    }

    const activeIds = activeSelection
      ? activeSelection.editedAbsenceId
        ? [activeSelection.editedAbsenceId]
        : activeSelection.selectedIds
      : [];

    if (!activeIds.length) {
      return [];
    }

    const selectedAbsences = absences.filter((absence) =>
      activeIds.includes(absence.id),
    );

    if (!selectedAbsences.length) {
      return [];
    }

    return absencesToEvents(
      activeSelection,
      collapseMonthViewAbsences(selectedAbsences),
      isDayView,
    );
  }, [absences, activeSelection, collapseMonthViewAbsences, isDayView, selectedAbsence?.isMultiEvent]);

  const timelineEvents = useMemo(() => {
    let transientEvents: EventInput[] = [];
    if (selectedSlot) {
      transientEvents = getTransientEventsForSlot(
        selectedSlot,
        absences,
        users,
        isDayView,
      );
    }

    const activeIds =
      activeSelection && !selectedAbsence?.isMultiEvent
        ? activeSelection.editedAbsenceId
          ? [activeSelection.editedAbsenceId]
          : activeSelection.selectedIds
        : [];
    const selectedIds = new Set(activeIds);
    const selectedDateKeys = new Set(
      absences
        .filter((absence) => selectedIds.has(absence.id))
        .map((absence) => `${absence.userId}:${absence.dateStr}`),
    );
    const visibleAbsences = collapseMonthViewAbsences(
      absences.filter((absence) => {
        if (isDayView) {
          return !selectedIds.has(absence.id);
        }
        return !selectedDateKeys.has(`${absence.userId}:${absence.dateStr}`);
      }),
    );
    const visibleAbsenceEvents = absencesToEvents(
      null,
      visibleAbsences,
      isDayView,
    );
    const visibleTodayBackgroundEvents = isHoveredDateToday
      ? []
      : todayBackgroundEvents;
    const backgroundEvents = isMonthView
      ? [
          ...weekendBackgroundEvents,
          ...holidayBackgroundEvents,
          ...visibleTodayBackgroundEvents,
          ...hoveredBackgroundEvents,
        ]
      : [];

    return [
      ...backgroundEvents,
      ...visibleAbsenceEvents,
      ...selectedAbsenceEvents,
      ...(transientEvents.length > 0 ? transientEvents : []),
    ];
  }, [
    absences,
    activeSelection,
    collapseMonthViewAbsences,
    holidayBackgroundEvents,
    hoveredBackgroundEvents,
    isDayView,
    isHoveredDateToday,
    isMonthView,
    selectedAbsence?.isMultiEvent,
    selectedAbsenceEvents,
    selectedSlot,
    todayBackgroundEvents,
    users,
    weekendBackgroundEvents,
  ]);

  return {
    activeSelection,
    holidayDatesSet,
    isHoveredDateToday,
    timelineEvents,
    timelineResources,
  };
}
